import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from "@floating-ui/react";
import type { ComponentType } from "react";
import {
  Children,
  cloneElement,
  isValidElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import MenuItem from "@/components/core/context-menu/menu-item";

interface Props {
  label?: string;
  nested?: boolean;
  children: ReactNode;
  triggerRef: React.RefObject<HTMLDivElement>;
}

type MenuSubComponents = {
  MenuItem: typeof MenuItem;
};

export type ContextMenuComponentType = ComponentType<Props> & MenuSubComponents;

const ContextMenu: ContextMenuComponentType = ({ children, triggerRef }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const listItemsRef = useRef<Array<HTMLButtonElement | null>>([]);
  const listContentRef = useRef(
    Children.map(children, (child) =>
      isValidElement(child) ? child.props.label : null
    ) as Array<string | null>
  );
  const allowMouseUpCloseRef = useRef(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset({ mainAxis: 5, alignmentAxis: 4 }),
      flip({
        fallbackPlacements: ["left-start"],
      }),
      shift({ padding: 10 }),
    ],
    placement: "right-start",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
  });

  const role = useRole(context, { role: "menu" });
  const dismiss = useDismiss(context);
  const listNavigation = useListNavigation(context, {
    listRef: listItemsRef,
    onNavigate: setActiveIndex,
    activeIndex,
  });
  const typeahead = useTypeahead(context, {
    enabled: isOpen,
    listRef: listContentRef,
    onMatch: setActiveIndex,
    activeIndex,
  });

  const { getFloatingProps, getItemProps } = useInteractions([
    role,
    dismiss,
    listNavigation,
    typeahead,
  ]);

  useEffect(() => {
    let timeout: number;

    function onContextMenu(e: MouseEvent) {
      e.preventDefault();

      refs.setPositionReference({
        getBoundingClientRect() {
          return {
            width: 0,
            height: 0,
            x: e.clientX,
            y: e.clientY,
            top: e.clientY,
            right: e.clientX,
            bottom: e.clientY,
            left: e.clientX,
          };
        },
      });

      setIsOpen(true);
      clearTimeout(timeout);

      allowMouseUpCloseRef.current = false;
      timeout = window.setTimeout(() => {
        allowMouseUpCloseRef.current = true;
      }, 300);
    }

    function onMouseUp() {
      if (allowMouseUpCloseRef.current) {
        setIsOpen(false);
      }
    }

    const trigger = triggerRef?.current;

    if (trigger) {
      trigger.addEventListener("contextmenu", onContextMenu);
      trigger.addEventListener("mouseup", onMouseUp);
    }
    return () => {
      if (trigger) {
        trigger.removeEventListener("contextmenu", onContextMenu);
        trigger.removeEventListener("mouseup", onMouseUp);
        clearTimeout(timeout);
      }
    };
  }, [refs, triggerRef]);

  return (
    <>
      <FloatingPortal>
        {isOpen && (
          <FloatingOverlay lockScroll style={{ zIndex: 9999 }}>
            <FloatingFocusManager
              context={context}
              initialFocus={refs.floating}
            >
              <div
                className="context-menu"
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}
              >
                {Children.map(
                  children,
                  (child, index) =>
                    isValidElement(child) &&
                    cloneElement(
                      child,
                      getItemProps({
                        tabIndex: activeIndex === index ? 0 : -1,
                        ref(node: HTMLButtonElement) {
                          listItemsRef.current[index] = node;
                        },
                        onClick() {
                          child.props.onClick?.();
                          setIsOpen(false);
                        },
                        onMouseUp() {
                          child.props.onClick?.();
                          setIsOpen(false);
                        },
                      })
                    )
                )}
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        )}
      </FloatingPortal>
      <style jsx>{`
        .context-menu {
          padding: 8px 4px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
          outline: 0;

          border-radius: 4px;
          border: 1px solid #191919;
          background: rgba(25, 25, 25, 0.88);
          backdrop-filter: blur(2px);
        }
      `}</style>
    </>
  );
};

ContextMenu.displayName = "ContextMenu";
ContextMenu.MenuItem = MenuItem;

export default ContextMenu;
